import React, { useEffect, useMemo, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { alertMonthLabel, monthsUntilContractEnd } from "../lib/contract";
import {
  Channel,
  Company,
  CompanySelection,
  Contact,
  ContactSelection,
  Opportunity,
  Pipeline,
  PipelineStage,
  Product,
  UserMini,
} from "../types/crm";
import { CompanyPicker } from "./CompanyPicker";
import { ContactPicker } from "./ContactPicker";

interface OpportunityFormModalProps {
  opportunity?: Opportunity;
  initialPipelineSlug?: string;
  onClose: () => void;
  onSaved: (opportunity: Opportunity) => void;
}

const FAROL_OPTIONS = [
  { id: "verde", label: "Verde", dot: "#22C55E", activeBg: "#DCFCE7", activeText: "#166534", desc: "Cliente saudável, renovação provável" },
  { id: "amarelo", label: "Amarelo", dot: "#F59E0B", activeBg: "#FEF3C7", activeText: "#92400E", desc: "Atenção — engajamento caindo" },
  { id: "vermelho", label: "Vermelho", dot: "#EF4444", activeBg: "#FEE2E2", activeText: "#DC2626", desc: "Risco de churn, ação imediata" },
] as const;

function fmtEvent(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

const inputClass =
  "mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

export function OpportunityFormModal({ opportunity, initialPipelineSlug, onClose, onSaved }: OpportunityFormModalProps) {
  const { username } = useAuth();
  const isEdit = !!opportunity;

  // Dados de referência (auto-carregados).
  const [companies, setCompanies] = useState<Company[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  const [companySelection, setCompanySelection] = useState<CompanySelection>(
    opportunity ? { type: "existing", id: opportunity.company } : { type: "new", data: { name: "", cnpj: "", website: "" } }
  );
  const [contactSelection, setContactSelection] = useState<ContactSelection>(
    opportunity && opportunity.primary_contact
      ? { type: "existing", id: opportunity.primary_contact }
      : { type: "new", data: { name: "", email: "", phone: "", role: "" } }
  );
  const [ownerId, setOwnerId] = useState<number | undefined>(opportunity?.owner);
  const [channelId, setChannelId] = useState<number | undefined>(opportunity?.channel ?? undefined);
  const [pipelineId, setPipelineId] = useState<number | undefined>(opportunity?.pipeline_detail?.id);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [stageId, setStageId] = useState<number | undefined>(opportunity?.stage);
  const [productIds, setProductIds] = useState<number[]>(opportunity?.products ?? []);
  const [mrrEstimated, setMrrEstimated] = useState(opportunity?.mrr_estimated ?? "0");
  const [nonRecurringEstimated, setNonRecurringEstimated] = useState(opportunity?.non_recurring_estimated ?? "0");
  const [resultValue, setResultValue] = useState<"ganho" | "perdido" | "">(opportunity?.result ?? "");
  const [meetingDate, setMeetingDate] = useState("");
  const [contractEnd, setContractEnd] = useState(opportunity?.contract_end ?? "");
  const [farolValue, setFarolValue] = useState<"verde" | "amarelo" | "vermelho" | "">(opportunity?.farol ?? "");
  const [showMore, setShowMore] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<Company[]>("/companies/"),
      api.get<Channel[]>("/channels/"),
      api.get<Product[]>("/products/"),
      api.get<Pipeline[]>("/pipelines/"),
      api.get<UserMini[]>("/users/"),
    ])
      .then(([c, ch, p, pl, u]) => {
        if (cancelled) return;
        setCompanies(c.data);
        setChannels(ch.data.filter((x) => x.is_active));
        setProducts(p.data.filter((x) => x.is_active));
        setPipelines(pl.data);
        setUsers(u.data.filter((x) => x.is_active));
        // Defaults de criação: pipeline da tela, responsável = usuário logado.
        setPipelineId((cur) => {
          if (cur) return cur;
          const bySlug = initialPipelineSlug ? pl.data.find((x) => x.slug === initialPipelineSlug) : undefined;
          return (bySlug ?? pl.data.find((x) => x.is_default) ?? pl.data[0])?.id;
        });
        setOwnerId((cur) => cur ?? u.data.find((x) => x.username === username)?.id);
      })
      .finally(() => !cancelled && setRefLoading(false));
    return () => {
      cancelled = true;
    };
  }, [initialPipelineSlug, username]);

  const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
  const selectedStage = stages.find((s) => s.id === stageId);

  useEffect(() => {
    if (!selectedPipeline) return;
    let cancelled = false;
    api.get<PipelineStage[]>(`/pipeline-stages/?pipeline=${selectedPipeline.slug}`).then((r) => {
      if (cancelled) return;
      setStages(r.data);
      setStageId((cur) => (cur && r.data.some((s) => s.id === cur) ? cur : r.data.find((s) => s.order === 1)?.id ?? r.data[0]?.id));
    });
    return () => {
      cancelled = true;
    };
  }, [selectedPipeline]);

  const toggleProduct = (id: number) =>
    setProductIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const monthsLeft = contractEnd ? monthsUntilContractEnd(contractEnd) : null;
  const contractAviso = monthsLeft !== null && monthsLeft <= 1;
  const contractHelp = useMemo(() => {
    if (!contractEnd) return { text: "O sistema avisa 1 mês antes do vencimento.", tone: "gray" as const };
    if (monthsLeft === null) return { text: "", tone: "gray" as const };
    if (monthsLeft < 0) return { text: "Contrato já vencido — priorize a renovação.", tone: "red" as const };
    if (monthsLeft === 0) return { text: "Vence este mês — alerta ativo em Follow-ups.", tone: "amber" as const };
    if (monthsLeft === 1) return { text: "Vence no próximo mês — alerta ativo em Follow-ups.", tone: "amber" as const };
    return { text: `Faltam ${monthsLeft} meses. Alerta dispara em ${alertMonthLabel(contractEnd)}.`, tone: "gray" as const };
  }, [contractEnd, monthsLeft]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const errors: Record<string, string> = {};
    // Único campo necessário: a empresa (identidade do lead). Demais opcionais.
    if (companySelection.type === "new" && !companySelection.data.name.trim()) {
      errors.company = "Informe ao menos o nome da empresa";
    }
    if (selectedStage?.is_closed && !selectedStage?.is_implementation && !resultValue) {
      errors.result = "Selecione o resultado para fechar";
    }
    if (selectedStage?.requires_meeting_date && !meetingDate) {
      errors.meeting_date = "Informe a data da reunião";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    try {
      const companyId =
        companySelection.type === "existing"
          ? companySelection.id
          : (await api.post<Company>("/companies/", companySelection.data)).data.id;

      let contactId: number | null = null;
      if (contactSelection.type === "existing") {
        contactId = contactSelection.id;
      } else if (contactSelection.data.name.trim()) {
        contactId = (await api.post<Contact>("/contacts/", { ...contactSelection.data, company: companyId })).data.id;
      }

      const payload: Record<string, unknown> = {
        company: companyId,
        primary_contact: contactId,
        owner: ownerId,
        channel: channelId ?? null,
        stage: stageId,
        products: productIds,
        mrr_estimated: Number(mrrEstimated) || 0,
        non_recurring_estimated: Number(nonRecurringEstimated) || 0,
        contract_end: selectedPipeline?.has_contract ? contractEnd : "",
        farol: selectedPipeline?.has_farol ? farolValue || null : null,
        needs_completion: isEdit ? opportunity!.needs_completion : !contactId,
        ...(selectedStage?.requires_meeting_date ? { meeting_date: meetingDate } : {}),
        ...(selectedStage?.is_closed && !selectedStage?.is_implementation ? { result: resultValue } : {}),
      };

      const response = opportunity
        ? await api.patch<Opportunity>(`/opportunities/${opportunity.id}/`, payload)
        : await api.post<Opportunity>("/opportunities/", payload);
      onSaved(response.data);
    } catch {
      setFormError("Não foi possível salvar. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 py-8">
      <div className="w-full max-w-lg rounded-xl border border-border-subtle bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">{isEdit ? "Editar oportunidade" : "Novo lead / oportunidade"}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        {!isEdit && (
          <p className="mt-1 text-sm text-slate-500">Só a empresa é necessária. O resto você completa depois.</p>
        )}

        {refLoading ? (
          <p className="mt-4 text-sm text-slate-500">Carregando...</p>
        ) : (
          <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
            <div>
              <CompanyPicker companies={companies} value={companySelection} onChange={setCompanySelection} />
              {fieldErrors.company && <p className="mt-1 text-sm text-red-600">{fieldErrors.company}</p>}
            </div>

            <ContactPicker companySelection={companySelection} value={contactSelection} onChange={setContactSelection} />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="pipeline" className="block text-sm font-medium text-slate-700">Pipeline</label>
                <select id="pipeline" value={pipelineId ?? ""} onChange={(e) => setPipelineId(Number(e.target.value))} className={inputClass}>
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="stage" className="block text-sm font-medium text-slate-700">Etapa</label>
                <select id="stage" value={stageId ?? ""} onChange={(e) => setStageId(Number(e.target.value))} className={inputClass}>
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="channel" className="block text-sm font-medium text-slate-700">Canal <span className="text-slate-400">(opcional)</span></label>
                <select id="channel" value={channelId ?? ""} onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : undefined)} className={inputClass}>
                  <option value="">—</option>
                  {channels.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div>
                <label htmlFor="owner" className="block text-sm font-medium text-slate-700">Responsável</label>
                <select id="owner" value={ownerId ?? ""} onChange={(e) => setOwnerId(e.target.value ? Number(e.target.value) : undefined)} className={inputClass}>
                  <option value="">—</option>
                  {users.map((u) => (<option key={u.id} value={u.id}>{u.username}</option>))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Produtos <span className="text-slate-400">(opcional)</span></label>
              <div className="mt-1 flex flex-wrap gap-3">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-1 text-sm text-slate-700">
                    <input type="checkbox" checked={productIds.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="mrr" className="block text-sm font-medium text-slate-700">MRR estimado <span className="text-slate-400">(opcional)</span></label>
              <input id="mrr" type="number" min="0" step="0.01" value={mrrEstimated} onChange={(e) => setMrrEstimated(e.target.value)} className={inputClass} />
            </div>

            {selectedStage?.requires_meeting_date && (
              <div>
                <label htmlFor="meeting" className="block text-sm font-medium text-slate-700">Data da reunião</label>
                <input id="meeting" type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} className={inputClass} />
                {fieldErrors.meeting_date && <p className="mt-1 text-sm text-red-600">{fieldErrors.meeting_date}</p>}
              </div>
            )}

            {selectedStage?.is_closed && !selectedStage?.is_implementation && (
              <div>
                <label className="block text-sm font-medium text-slate-700">Resultado</label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="result" checked={resultValue === "ganho"} onChange={() => setResultValue("ganho")} /> Ganho
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="radio" name="result" checked={resultValue === "perdido"} onChange={() => setResultValue("perdido")} /> Perdido
                  </label>
                </div>
                {fieldErrors.result && <p className="mt-1 text-sm text-red-600">{fieldErrors.result}</p>}
              </div>
            )}

            {/* Mais detalhes */}
            <button type="button" onClick={() => setShowMore((v) => !v)} className="text-sm font-medium text-primary hover:underline">
              {showMore ? "Menos detalhes ▲" : "Mais detalhes ▼"}
            </button>
            {showMore && (
              <div className="space-y-4 rounded-[10px] border border-border-subtle bg-surface p-4">
                <div>
                  <label htmlFor="nr" className="block text-sm font-medium text-slate-700">Não recorrente</label>
                  <input id="nr" type="number" min="0" step="0.01" value={nonRecurringEstimated} onChange={(e) => setNonRecurringEstimated(e.target.value)} className={inputClass} />
                </div>
                {selectedPipeline?.has_contract && (
                  <>
                    <div>
                      <label htmlFor="contract" className="block text-sm font-medium text-slate-700">Mês de fim do contrato</label>
                      <input id="contract" type="month" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)}
                        className="mt-1 block w-full rounded-[10px] border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                        style={contractAviso ? { borderColor: "#FDE68A", backgroundColor: "#FFFBEB" } : { borderColor: "#E2E8F0" }} />
                      <p className="mt-1 text-xs" style={{ color: contractHelp.tone === "amber" ? "#92400E" : contractHelp.tone === "red" ? "#DC2626" : "#94A3B8" }}>{contractHelp.text}</p>
                    </div>
                    {selectedPipeline?.has_farol && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700">Farol do cliente</label>
                        <div className="mt-1 flex gap-2">
                          {FAROL_OPTIONS.map((opt) => {
                            const active = farolValue === opt.id;
                            return (
                              <button key={opt.id} type="button" onClick={() => setFarolValue(active ? "" : opt.id)}
                                className="flex flex-1 items-center justify-center gap-1 rounded-[10px] px-2 py-2 text-xs"
                                style={active ? { backgroundColor: opt.activeBg, color: opt.activeText, border: `1.5px solid ${opt.dot}`, fontWeight: 600 } : { backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", color: "#64748B" }}>
                                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: opt.dot }} /> {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="mt-1 text-xs text-slate-400">{FAROL_OPTIONS.find((o) => o.id === farolValue)?.desc ?? "Selecione o status de saúde do cliente"}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Histórico */}
            {isEdit && opportunity!.events.length > 0 && (
              <div className="rounded-[10px] border border-border-subtle p-3">
                <p className="text-sm font-semibold text-slate-700">Histórico</p>
                <ul className="mt-2 space-y-1.5">
                  {[...opportunity!.events].reverse().map((ev) => (
                    <li key={ev.id} className="text-xs text-slate-500">
                      <span className="text-slate-700">{fmtEvent(ev.created_at)}</span>
                      {" · "}
                      {ev.from_stage_name
                        ? `${ev.from_pipeline !== ev.to_pipeline ? `${ev.from_pipeline} / ` : ""}${ev.from_stage_name} → ${ev.to_pipeline !== ev.from_pipeline ? `${ev.to_pipeline} / ` : ""}${ev.to_stage_name}`
                        : `criado em ${ev.to_pipeline ?? ""} / ${ev.to_stage_name}`}
                      {ev.user_username ? ` · ${ev.user_username}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface">Cancelar</button>
              <button type="submit" disabled={isSubmitting} className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60">
                {isSubmitting ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
